import { useEffect, useRef } from 'react';

import type { AnimationItem } from 'lottie-web';

import { logger } from '@/utils/logger/logger';

import {
  REFRESH_SPINNER_ANIMATION,
  REFRESH_SUCCESS_ANIMATION,
} from './refresh-spinner-animation-data';

interface RefreshLottieIconProps {
  /** 'loading' = looping spinner, 'success' = one-shot checkmark draw */
  phase: 'loading' | 'success';
  size?: number;
}

const TAG = 'RefreshLottieIcon:';

export function RefreshLottieIcon({ phase, size = 20 }: RefreshLottieIconProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<AnimationItem | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const lottie = await import('lottie-web');
        if (cancelled || !containerRef.current) return;

        animRef.current?.destroy();

        animRef.current = lottie.default.loadAnimation({
          container: containerRef.current,
          renderer: 'svg',
          loop: phase === 'loading',
          autoplay: true,
          animationData:
            phase === 'loading' ? REFRESH_SPINNER_ANIMATION : REFRESH_SUCCESS_ANIMATION,
        });
      } catch (err) {
        logger.error(TAG, 'Failed to load animation', err);
      }
    };

    load();

    return () => {
      cancelled = true;
      animRef.current?.destroy();
      animRef.current = null;
    };
  }, [phase]);

  return <div ref={containerRef} style={{ width: size, height: size, flexShrink: 0 }} />;
}
